import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import NotificationsTab from '@/components/NotificationsTab';
import { DomainConfigModal } from '@/components/DomainConfigModal';
import { TrainingVideoModal } from '@/components/TrainingVideoModal';
import { useLocationTrainingVideo } from '@/hooks/useLocationTrainingVideo';
import {
  Building2,
  Palette,
  Globe,
  Mail,
  Settings,
  CheckCircle2,
  AlertCircle,
  Users,
  ExternalLink,
  Copy,
  PlayCircle,
} from 'lucide-react';
import type { Database } from '@/integrations/supabase/types';

type Client = Database['public']['Tables']['clients']['Row'];

interface BusinessPortalSettings {
  branding: {
    primaryColor?: string;
    secondaryColor?: string;
    logo?: string;
    companyName?: string;
    tagline?: string;
  };
  domain?: string;
  subdomain?: string;
  isBusinessPortal: boolean;
  portalEnabled: boolean;
  onboardingComplete: boolean;
}

export default function BusinessPortal() {
  const [businessPortal, setBusinessPortal] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<BusinessPortalSettings>({
    branding: {
      primaryColor: '#3b82f6',
      secondaryColor: '#64748b',
      companyName: '',
      tagline: 'Powered by CallSparker',
    },
    isBusinessPortal: true,
    portalEnabled: true,
    onboardingComplete: false,
  });
  const { user, apiKeys } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { hasVideo, displayTitle } =
    useLocationTrainingVideo('business-portal');
  const [domainModalOpen, setDomainModalOpen] = useState(false);
  const [domainFeedback, setDomainFeedback] = useState<{
    title: string;
    message: string;
    dnsInstructions?: string;
    txtValue?: string;
  } | null>(null);
  const [domainLoading, setDomainLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchBusinessPortal();
    }
  }, [user]);

  const fetchBusinessPortal = async () => {
    try {
      setLoading(true);

      // Look for existing business portal
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', user?.id)
        .eq('settings->>is_business_portal', 'true')
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setBusinessPortal(data);
        const clientSettings = (data.settings as any) || {};
        setSettings({
          branding: (data.branding as any) || {
            primaryColor: '#3b82f6',
            secondaryColor: '#64748b',
            companyName: '',
            tagline: 'Powered by CallSparker',
          },
          domain: data.domain || '',
          subdomain: data.subdomain || '',
          isBusinessPortal: true,
          portalEnabled: clientSettings.portal?.enabled || false,
          onboardingComplete:
            clientSettings.portal?.onboardingComplete || false,
        });
      }
    } catch (error: any) {
      console.error('Error fetching business portal:', error);
      toast({
        title: 'Error',
        description: 'Failed to load business portal settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveBusinessPortal = async () => {
    try {
      setSaving(true);

      // Merge existing settings with current ones, preserving notification settings
      const existingSettings = (businessPortal?.settings as any) || {};
      const mergedSettings = {
        ...existingSettings,
        is_business_portal: true,
        portal: {
          ...existingSettings.portal,
          enabled: settings.portalEnabled,
          onboardingComplete: true,
        },
      };

      // Prepare base data without domain/subdomain
      const baseData = {
        name: settings.branding.companyName || 'Business Portal',
        email: user?.email || '',
        user_id: user?.id,
        status: 'active',
        branding: settings.branding,
        settings: mergedSettings,
      };

      // Check if domain or subdomain needs to be added/updated
      const oldDomain = businessPortal?.domain || '';
      const oldSubdomain = businessPortal?.subdomain || '';
      const domainChanged = settings.domain && settings.domain !== oldDomain;
      const subdomainChanged =
        settings.subdomain && settings.subdomain !== oldSubdomain;
      const needDomainAdd =
        domainChanged ||
        subdomainChanged ||
        (!businessPortal && (settings.domain || settings.subdomain));

      // Always save base data first
      let updatedBusinessPortal;
      if (businessPortal) {
        const { error } = await supabase
          .from('clients')
          .update(baseData)
          .eq('id', businessPortal.id);

        if (error) throw error;
        updatedBusinessPortal = { ...businessPortal, ...baseData };

        // IMPORTANT: Propagate branding changes to any clients that either already
        // opted in or haven't been marked yet. Also set the flag so they continue
        // automatically inheriting changes. Exclude the portal row itself.
        const { data: updatedClients, error: inheritError } = await supabase
          .from('clients')
          .update({
            branding: settings.branding,
            settings: { inherits_business_branding: true },
          })
          .eq('user_id', user?.id)
          .neq('settings->>inherits_business_branding', 'false')
          .neq('id', businessPortal.id) // exclude the portal row itself
          .select('id');

        if (inheritError) {
          console.error('Error updating inherited clients:', inheritError);
          // Don't throw - let the portal save succeed even if inheritance update fails
        } else {
          console.log(
            `Updated branding for ${updatedClients?.length ?? 0} inherited clients`,
            updatedClients,
          );
          if (updatedClients && updatedClients.length > 0) {
            toast({
              title: 'Branding Applied',
              description: `Portal branding pushed to ${updatedClients.length} client${
                updatedClients.length === 1 ? '' : 's'
              }.`,
            });
          }
        }
      } else {
        const { data, error } = await supabase
          .from('clients')
          .insert(baseData)
          .select()
          .single();

        if (error) throw error;
        updatedBusinessPortal = data;
        setBusinessPortal(data); // Update local state immediately

        // If this is the first time a portal is created, stray existing clients may
        // not have the inherits_business_branding flag set.  Automatically mark them
        // and apply branding/name so they will follow the portal going forward.
        // (We also ignore any client that explicitly opted-out by setting the flag
        // to `false`.)
        const { data: markedClients, error: markError } = await supabase
          .from('clients')
          .update({
            settings: { inherits_business_branding: true },
            branding: settings.branding,
          })
          .eq('user_id', user?.id)
          .neq('id', data.id) // don't touch the portal row itself
          .neq('settings->>inherits_business_branding', 'false')
          .select('id');

        if (markError) {
          console.error(
            'Error marking existing clients to inherit branding:',
            markError,
          );
        } else {
          console.log(
            `Marked ${markedClients?.length ?? 0} existing clients to inherit branding`,
            markedClients,
          );
          if (markedClients && markedClients.length > 0) {
            toast({
              title: 'Clients Updated',
              description: `Configured ${markedClients.length} existing client${
                markedClients.length === 1 ? '' : 's'
              } to inherit portal branding.`,
            });
          }
        }
      }

      // Additionally save email API key to user_api_keys if provided (via manage-api-keys)
      const notifications = mergedSettings.portal?.notifications || {};
      if (notifications.emailProvider && notifications.emailApiKey) {
        const apiKeyName =
          notifications.emailProvider === 'smtp2go'
            ? 'smtp2go_api_key'
            : 'api_key';
        const { data: saveApiData, error: saveApiError } =
          await supabase.functions.invoke('manage-api-keys', {
            body: {
              action: 'save',
              provider: notifications.emailProvider,
              key_name: apiKeyName,
              value: notifications.emailApiKey,
            },
          });

        if (saveApiError) {
          console.error(
            'Error saving email API key to user_api_keys:',
            saveApiError,
          );
          toast({
            title: 'Warning',
            description:
              'Business portal saved, but failed to save email API key to secure storage: ' +
              saveApiError.message,
            variant: 'destructive',
          });
        } else if (saveApiData.success) {
          toast({
            title: 'Success',
            description: 'Email API key saved securely',
          });
        }
      }

      // Now add domain if needed (client row exists, so no 404)
      if (needDomainAdd) {
        // Prioritize domain if both are set/changed; adjust logic if needed for mutual exclusivity
        const type = domainChanged || settings.domain ? 'domain' : 'subdomain';
        const value = type === 'domain' ? settings.domain : settings.subdomain;

        setDomainModalOpen(true); // Open modal immediately
        setDomainLoading(true); // Show spinner

        try {
          const { data: addDomainData, error: addDomainError } =
            await supabase.functions.invoke('add-custom-domain', {
              body: { domain: value, type },
            });

          setDomainLoading(false);

          if (addDomainError) {
            let message =
              addDomainError.message || 'Failed to configure domain';
            if (message.includes('Vercel add error')) {
              // Parse Vercel-specific errors
              try {
                const vercelErr = JSON.parse(
                  message.split('Vercel add error: ')[1],
                );
                if (
                  vercelErr.error?.code === 'domain_not_available' ||
                  vercelErr.error?.message.includes('ownership')
                ) {
                  message =
                    'Domain ownership could not be verified. Ensure you own the domain and have added the required DNS records (A @ to 216.150.1.1 for apex domains).';
                } else if (vercelErr.error?.code === 'forbidden') {
                  message =
                    'API access denied—check Vercel token scopes and team settings.';
                } else {
                  message = `Vercel error: ${
                    vercelErr.error?.message || 'Unknown issue'
                  }.`;
                }
              } catch {}
            }
            setDomainFeedback({
              title: 'Domain Error',
              message,
            });
            // Optionally rollback or handle failure (e.g., update DB to remove domain if partial success)
            return; // Stop - don't proceed on failure
          } else {
            // Extract TXT value for copy button (parse from instructions if present)
            const txtMatch =
              addDomainData.dnsInstructions?.match(/value "([^"]+)"/) ||
              addDomainData.message?.match(/value "([^"]+)"/);
            const txtValue = txtMatch ? txtMatch[1] : null;

            setDomainFeedback({
              title: addDomainData.success ? 'Domain Configuration' : 'Error',
              message: addDomainData.message,
              dnsInstructions: addDomainData.dnsInstructions,
              txtValue,
            });

            // Update local state with new domain/subdomain
            setSettings((prev) => ({
              ...prev,
              [type]: value,
            }));
            setBusinessPortal((prev) =>
              prev ? { ...prev, [type]: value } : null,
            );
          }
        } catch (err) {
          setDomainLoading(false);
          setDomainFeedback({
            title: 'Domain Error',
            message: 'An unexpected error occurred. Please try again.',
          });
          return; // Stop on failure
        }
      }

      setSettings((prev) => ({ ...prev, onboardingComplete: true }));

      toast({
        title: 'Success',
        description: 'Business portal settings saved successfully',
      });

      // Notify other UI (e.g. clients list) that portal has changed so they can refresh
      window.dispatchEvent(new Event('businessPortalUpdated'));

      // Optional: Refetch to sync any DB changes
      fetchBusinessPortal();
    } catch (error: any) {
      console.error('Error saving business portal:', error);
      toast({
        title: 'Error',
        description: 'Failed to save business portal settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const generateAccessLink = () => {
    const baseUrl = settings.domain
      ? `https://${settings.domain}`
      : settings.subdomain
        ? `https://${settings.subdomain}.callsparker.com`
        : `${window.location.origin}`;

    return `${baseUrl}/portal/dashboard`;
  };

  const copyAccessLink = () => {
    navigator.clipboard.writeText(generateAccessLink());
    toast({
      title: 'Success',
      description: 'Portal access link copied to clipboard',
    });
  };

  if (loading) {
    return (
      <div className='container mx-auto p-6 space-y-6'>
        <div className='animate-pulse'>
          <div className='h-8 bg-muted rounded w-1/3 mb-2'></div>
          <div className='h-4 bg-muted rounded w-2/3'></div>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          {[1, 2, 3].map((i) => (
            <Card key={i} className='animate-pulse'>
              <CardContent className='p-6'>
                <div className='h-16 bg-muted rounded'></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const isSetupComplete =
    settings.onboardingComplete && settings.branding.companyName;

  // Check email provider from portal notification settings
  const portalNotifications =
    (businessPortal?.settings as any)?.portal?.notifications || {};
  const hasEmailProvider = !!(
    portalNotifications.emailProvider &&
    portalNotifications.emailApiKey &&
    portalNotifications.fromEmail
  );

  return (
    <div className='p-6 space-y-8'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-foreground flex items-center gap-3'>
            <Building2 className='h-8 w-8' />
            Business Portal Setup
          </h1>
          <p className='text-muted-foreground'>
            Configure your white-label client portal with branding and domain
            settings
          </p>
        </div>

        {hasVideo && (
          <div className='mb-6'>
            <TrainingVideoModal
              trigger={
                <Button variant='outline' size='sm'>
                  <PlayCircle className='h-4 w-4 mr-2' />
                  {displayTitle || 'View Training'}
                </Button>
              }
              locationKey='business-portal'
            />
          </div>
        )}

        {isSetupComplete && (
          <div className='flex items-center gap-2'>
            <Button variant='outline' onClick={copyAccessLink}>
              <Copy className='h-4 w-4 mr-2' />
              Copy Access Link
            </Button>
            <Button onClick={() => navigate('/clients')}>
              <Users className='h-4 w-4 mr-2' />
              Manage Clients
            </Button>
          </div>
        )}
      </div>

      {/* Setup Progress */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2'>
              {settings.branding.companyName ? (
                <CheckCircle2 className='h-5 w-5 text-green-500' />
              ) : (
                <AlertCircle className='h-5 w-5 text-orange-500' />
              )}
              <div>
                <div className='font-medium'>Branding</div>
                <div className='text-sm text-muted-foreground'>
                  {settings.branding.companyName ? 'Configured' : 'Required'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2'>
              {settings.domain || settings.subdomain ? (
                <CheckCircle2 className='h-5 w-5 text-green-500' />
              ) : (
                <AlertCircle className='h-5 w-5 text-orange-500' />
              )}
              <div>
                <div className='font-medium'>Domain</div>
                <div className='text-sm text-muted-foreground'>
                  {settings.domain || settings.subdomain
                    ? 'Configured'
                    : 'Optional'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2'>
              {hasEmailProvider ? (
                <CheckCircle2 className='h-5 w-5 text-green-500' />
              ) : (
                <AlertCircle className='h-5 w-5 text-orange-500' />
              )}
              <div>
                <div className='font-medium'>Email Provider</div>
                <div className='text-sm text-muted-foreground'>
                  {hasEmailProvider ? 'Configured' : 'Recommended'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2'>
              {isSetupComplete ? (
                <CheckCircle2 className='h-5 w-5 text-green-500' />
              ) : (
                <AlertCircle className='h-5 w-5 text-orange-500' />
              )}
              <div>
                <div className='font-medium'>Portal Status</div>
                <div className='text-sm text-muted-foreground'>
                  {isSetupComplete ? 'Ready' : 'Incomplete'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Configuration Tabs */}
      <Tabs defaultValue='branding' className='space-y-6'>
        <TabsList className='grid w-full grid-cols-4'>
          <TabsTrigger value='branding' className='flex items-center gap-2'>
            <Palette className='h-4 w-4' />
            Branding
          </TabsTrigger>
          <TabsTrigger value='domain' className='flex items-center gap-2'>
            <Globe className='h-4 w-4' />
            Domain
          </TabsTrigger>
          <TabsTrigger
            value='notifications'
            className='flex items-center gap-2'
          >
            <Mail className='h-4 w-4' />
            Notifications
          </TabsTrigger>
          <TabsTrigger
            value='configuration'
            className='flex items-center gap-2'
          >
            <Settings className='h-4 w-4' />
            Configuration
          </TabsTrigger>
        </TabsList>

        <TabsContent value='branding' className='space-y-6'>
          <Card>
            <CardHeader>
              <CardTitle>Portal Branding</CardTitle>
              <CardDescription>
                Customize how your client portal looks and feels
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='company-name'>Company Name *</Label>
                  <Input
                    id='company-name'
                    value={settings.branding.companyName || ''}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        branding: {
                          ...prev.branding,
                          companyName: e.target.value,
                        },
                      }))
                    }
                    placeholder='Your Company Name'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='tagline'>Tagline</Label>
                  <Input
                    id='tagline'
                    value={settings.branding.tagline || ''}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        branding: { ...prev.branding, tagline: e.target.value },
                      }))
                    }
                    placeholder='Your company tagline'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='primary-color'>Primary Color</Label>
                  <div className='flex items-center gap-2'>
                    <Input
                      type='color'
                      id='primary-color'
                      value={settings.branding.primaryColor || '#3b82f6'}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          branding: {
                            ...prev.branding,
                            primaryColor: e.target.value,
                          },
                        }))
                      }
                      className='w-16 h-10'
                    />
                    <Input
                      value={settings.branding.primaryColor || '#3b82f6'}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          branding: {
                            ...prev.branding,
                            primaryColor: e.target.value,
                          },
                        }))
                      }
                      placeholder='#3b82f6'
                    />
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='secondary-color'>Secondary Color</Label>
                  <div className='flex items-center gap-2'>
                    <Input
                      type='color'
                      id='secondary-color'
                      value={settings.branding.secondaryColor || '#64748b'}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          branding: {
                            ...prev.branding,
                            secondaryColor: e.target.value,
                          },
                        }))
                      }
                      className='w-16 h-10'
                    />
                    <Input
                      value={settings.branding.secondaryColor || '#64748b'}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          branding: {
                            ...prev.branding,
                            secondaryColor: e.target.value,
                          },
                        }))
                      }
                      placeholder='#64748b'
                    />
                  </div>
                </div>
              </div>

              {/* Logo Upload - Placeholder for now */}
              <div className='space-y-2'>
                <Label>Company Logo</Label>
                <div className='border-2 border-dashed border-muted rounded-lg p-8 text-center'>
                  <div className='text-muted-foreground'>
                    Logo upload coming soon - for now, company name will be
                    displayed
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='domain' className='space-y-6'>
          <Card>
            <CardHeader>
              <CardTitle>Domain Configuration</CardTitle>
              <CardDescription>
                Connect your custom domain for a professional white-label
                experience
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='custom-domain'>Custom Domain</Label>
                  <Input
                    id='custom-domain'
                    value={settings.domain || ''}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        domain: e.target.value,
                      }))
                    }
                    placeholder='portal.yourcompany.com'
                  />
                  <p className='text-sm text-muted-foreground'>
                    Use your own domain for the client portal (requires DNS
                    setup)
                  </p>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='subdomain'>Subdomain (Alternative)</Label>
                  <Input
                    id='subdomain'
                    value={settings.subdomain || ''}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        subdomain: e.target.value,
                      }))
                    }
                    placeholder='yourcompany'
                  />
                  <p className='text-sm text-muted-foreground'>
                    Use a CallSparker subdomain: yourcompany.callsparker.com
                  </p>
                </div>
              </div>

              {/* DNS Setup Instructions */}
              <div className='p-4 bg-muted rounded-lg'>
                <h4 className='font-medium mb-3'>DNS Setup Instructions</h4>
                <div className='space-y-3 text-sm'>
                  <div>
                    <strong>Step 1:</strong> If using Custom Domain, add the DNS
                    records at your domain registrar: (NOT needed for subdomain)
                  </div>
                  <div className='bg-background p-3 rounded border font-mono text-xs space-y-1'>
                    <div>Type: A | Name: @ | Value: 216.150.1.1</div>
                  </div>
                  <div>
                    <strong>For Cloudflare users:</strong> Set "Proxy status" to
                    "DNS only" (gray cloud)
                  </div>
                  <div>
                    <strong>IMPORTANT!</strong> - We use our own proxy so
                    setting to orange cloud could impact performance
                  </div>
                  <div>
                    <strong>Step 2:</strong> DNS propagation can take 24-48
                    hours
                  </div>
                  <div>
                    <strong>Step 3:</strong> SSL certificates are automatically
                    provisioned on our end
                  </div>
                </div>
              </div>

              {/* Portal Preview */}
              <div className='space-y-3'>
                <div className='font-medium'>Portal Access</div>
                <div className='p-3 bg-background rounded border'>
                  <div className='text-sm text-muted-foreground mb-2'>
                    Current portal URL:
                  </div>
                  <div className='font-mono text-sm break-all'>
                    {generateAccessLink()}
                  </div>
                </div>

                {isSetupComplete && (
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      onClick={() =>
                        window.open(`/portal/dashboard?preview=1`, '_blank')
                      }
                    >
                      <ExternalLink className='h-4 w-4 mr-2' />
                      Preview Client Portal
                    </Button>
                    <Button variant='outline' onClick={copyAccessLink}>
                      <Copy className='h-4 w-4 mr-2' />
                      Copy Link
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='notifications' className='space-y-6'>
          <NotificationsTab
            businessPortal={businessPortal}
            onSettingsChange={(newSettings) => {
              if (businessPortal) {
                setBusinessPortal({
                  ...businessPortal,
                  settings: {
                    ...((businessPortal.settings as Record<string, any>) || {}),
                    ...newSettings,
                  } as any,
                });
              }
            }}
          />
        </TabsContent>

        <TabsContent value='configuration' className='space-y-6'>
          <Card>
            <CardHeader>
              <CardTitle>Portal Configuration</CardTitle>
              <CardDescription>
                General settings for your client portal
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='flex items-center justify-between p-4 border rounded-lg'>
                <div>
                  <div className='font-medium'>Portal Status</div>
                  <div className='text-sm text-muted-foreground'>
                    Enable or disable the client portal
                  </div>
                </div>
                <Badge
                  variant={settings.portalEnabled ? 'default' : 'secondary'}
                >
                  {settings.portalEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>

              <div className='space-y-2'>
                <Label>Portal Features</Label>
                <div className='space-y-2 text-sm text-muted-foreground'>
                  <div>• Branded client dashboard</div>
                  <div>
                    • Filtered action center (client-specific data only)
                  </div>
                  <div>• Real-time notifications for actionable items</div>
                  <div>• Read-only campaign visibility</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className='flex justify-end'>
        <Button
          onClick={saveBusinessPortal}
          disabled={saving || !settings.branding.companyName}
          size='lg'
        >
          {saving
            ? 'Saving...'
            : businessPortal
              ? 'Update Business Portal'
              : 'Create Business Portal'}
        </Button>
      </div>

      <DomainConfigModal
        open={domainModalOpen}
        onOpenChange={setDomainModalOpen}
        loading={domainLoading}
        feedback={domainFeedback}
      />
    </div>
  );
}
